import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "../../constents/axios";
import { useLocation } from "react-router-dom";
import { S3Client } from "@aws-sdk/client-s3";
import { Upload } from "@aws-sdk/lib-storage";

const ZoomToOracleUpload = () => {
  const location = useLocation();
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadedUrl, setUploadedUrl] = useState("");
  const [materialId, setMaterialId] = useState("");
  const [uploadedCourses, setUploadedCourses] = useState([]);
  const [isUploaded, setIsUploaded] = useState(false);
  const [transcodeStatus, setTranscodeStatus] = useState("");
  const [mp4TranscodeStatus, setMp4TranscodeStatus] = useState("");
  const [liveData, setLiveData] = useState("");
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [pdfFiles, setPdfFiles] = useState([]);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfMessage, setPdfMessage] = useState("");
  const [uploadedPdfs, setUploadedPdfs] = useState([]);
  const [showReuploadModal, setShowReuploadModal] = useState(false);
  const [reuploadAllowed, setReuploadAllowed] = useState(false);
  const [reuploadWaitSeconds, setReuploadWaitSeconds] = useState(null);
  const [reuploadReason, setReuploadReason] = useState("");
  const pollRef = useRef(null);
  const pollTimeoutRef = useRef(null);
  const pollControllerRef = useRef(null);

  const getStatusBadge = (status) => {
    const s = String(status || "Pending").toLowerCase();
    if (s === "completed") {
      return { bg: "#dcfce7", color: "#166534", label: "Completed" };
    }
    if (s === "failed") {
      return { bg: "#fee2e2", color: "#991b1b", label: "Failed" };
    }
    if (s === "in progress" || s === "in_progress" || s === "processing") {
      return { bg: "#dbeafe", color: "#1e40af", label: "In Progress" };
    }
    // Default "Pending" is shown as "In Progress" since it's actively being processed
    return { bg: "#dbeafe", color: "#1e40af", label: "In Progress" };
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
      pollControllerRef.current?.abort();
    };
  }, []);

  // 🔄 Decode meeting ID from URL
  const extractAndDecodeLiveId = (path) => {
    const base64Str = path.split("/").pop();
    try {
      return JSON.parse(atob(base64Str));
    } catch (e) {
      console.error("Failed to decode liveId:", e);
      return null;
    }
  };

  const data = extractAndDecodeLiveId(location.pathname);
  const liveId = data?.meeting_id ?? "";

  // 🔄 Fetch details
  const getLiveData = useCallback(async () => {
    try {
      const response = await axios.get(`/getLiveVideoData/${liveId}`);
      const d = response.data;
      setReuploadAllowed(d.reuploadAllowed === true);
      setReuploadWaitSeconds(d.reuploadWaitSeconds ?? null);
      setReuploadReason(d.reuploadReason || "");
      setLiveData(d.videoData);
      setSelectedCourses(JSON.parse(d.videoData.available_course));

      // If this live video was already uploaded, show it directly
      if (d.isUploaded && d.uploadedVideo) {
        setIsUploaded(true);
        setUploadedUrl(d.uploadedVideo);
        setMaterialId(d.videoData?.material_id || "");
        setUploadedCourses(d.uploadedCourses || []);
        setTranscodeStatus(d.transcodeStatus || "Pending");
        setMp4TranscodeStatus(d.mp4TranscodeStatus || "Pending");
      }

      // Build full S3 URLs for linked PDFs
      const bucket = process.env.REACT_APP_AWS_S3_BUCKET;
      const region = process.env.REACT_APP_AWS_REGION;
      const baseUrl = `https://${bucket}.s3.${region}.amazonaws.com/learning_materials/`;
      const pdfs = (d.uploadedPdfs || []).map((p) => ({
        ...p,
        url: baseUrl + p.video_key,
      }));
      setUploadedPdfs(pdfs);
    } catch (error) {
      console.error("Error fetching live data:", error);
      setReuploadAllowed(false);
    }
  }, [liveId]);

  useEffect(() => {
    if (liveId) getLiveData();
  }, [liveId, getLiveData]);

  useEffect(() => {
    if (reuploadAllowed || !(reuploadWaitSeconds > 0)) return;
    const timeout = setTimeout(() => getLiveData(), Math.min(reuploadWaitSeconds * 1000 + 1000, 2147483647));
    return () => clearTimeout(timeout);
  }, [reuploadAllowed, reuploadWaitSeconds, getLiveData]);

  // 🚀 Upload flow
  const handleUpload = async () => {
    if (!liveId) {
      setMessage("⚠️ Meeting ID missing.");
      return;
    }

    // Stop any existing polling/timeout
    if (pollRef.current) clearInterval(pollRef.current);
    if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
    pollControllerRef.current?.abort();
    const controller = new AbortController();
    pollControllerRef.current = controller;

    setLoading(true);
    setProgress(0);
    setUploadedUrl("");
    setMaterialId("");
    setUploadedCourses([]);
    setMessage("⏳ Starting upload job…");

    try {
      // 🔥 Call backend to start downloading Zoom video + uploading to Oracle
      const cleaned = liveId.replace(/%20/g, " ");
      let sdkNumber = 0;
      const subjectLower = (liveData.subject || "").toLowerCase();

      if (
        subjectLower.includes("paper 1") ||
        subjectLower.includes("testing") ||
        subjectLower.includes("commerce") ||
        subjectLower.includes("arabic") ||
        subjectLower.includes("history") ||
        subjectLower.includes("political science") ||
        subjectLower.includes("economics") ||
        subjectLower.includes("malayalam") ||
        subjectLower.includes("management") ||
        subjectLower.includes("psychology") ||
        subjectLower.includes("sociology") ||
        subjectLower.includes("english") ||
        subjectLower.includes("computer science") ||
        subjectLower.includes("ktet") ||
        subjectLower.includes("mass communication") ||
        subjectLower.includes("journalism") 
      ) {
        // aiferwebinar@gmail.com
        sdkNumber = 0;
      // } else if (
      // ) {
      //   // nashwaaifer@gmail.com
      //   sdkNumber = 2;
      } else if (subjectLower.includes("csir")) {
        // lifescienceaifer@gmail.com
        sdkNumber = 3;
      }

      console.log("meetingId", cleaned, "sdkNumber", sdkNumber);

      const startRes = await axios.post(
        "/zoom/zoomDownload",
        { meetingId: cleaned, sdkNumber },
        {
          headers: {
            autherisation: `V9YlI4AgRJa97h9JT45acmijLliEcCEMnxZLtL7VlnWVETZuitk2MYoj0pCcrmHa`,
          },
        }
      );

      console.log(startRes.data);

      if (!startRes.data.success || !startRes.data.jobId) {
        setMessage("❌ Failed to start upload job.");
        setProgress(0);
        setLoading(false);
        return;
      }

      const jobId = startRes.data.jobId;
      setMessage("⏳ Upload job started. Downloading from Zoom…");

      // Poll job status every 3 seconds
      const statusHeaders = {
        autherisation: `V9YlI4AgRJa97h9JT45acmijLliEcCEMnxZLtL7VlnWVETZuitk2MYoj0pCcrmHa`,
      };

      let requestInFlight = false;
      let consecutiveErrors = 0;
      let nextPollAt = 0;

      pollRef.current = setInterval(async () => {
        if (requestInFlight || controller.signal.aborted || Date.now() < nextPollAt) return;
        requestInFlight = true;
        try {
          const statusRes = await axios.get(
            `/zoom/zoomDownloadStatus/${jobId}`,
            { headers: statusHeaders, timeout: 20000, signal: controller.signal }
          );
          if (controller.signal.aborted) return;
          consecutiveErrors = 0;
          nextPollAt = 0;

          const job = statusRes.data?.job;
          if (!job) {
            setMessage("❌ No job status returned.");
            clearInterval(pollRef.current);
            pollRef.current = null;
            if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
            setLoading(false);
            setProgress(0);
            return;
          }

          if (typeof job.progress === "number") {
            setProgress(job.progress);
          }

          if (job.status === "processing") {
            const stepText =
              job.step === "fetching_zoom_token"
                ? "Authenticating with Zoom…"
                : job.step === "fetching_recording_metadata"
                ? "Fetching recording metadata…"
                : job.step === "downloading_from_zoom"
                ? "Downloading video from Zoom…"
                : job.step === "uploading_to_oracle"
                ? "Uploading video to Oracle…"
                : "Processing…";
            setMessage(`⏳ ${stepText}`);
            return;
          }

          if (job.status === "completed") {
            clearInterval(pollRef.current);
            pollRef.current = null;
            if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
            setProgress(100);
            const oracleUrl = job.url;
            setUploadedUrl(oracleUrl);
            setMessage("🎉 Video uploaded to Oracle! Saving to Student Journey…");

            try {
              const savedMaterialId = await storeToStudentJourney(oracleUrl);
              if (savedMaterialId) {
                setMaterialId(savedMaterialId);
                setIsUploaded(true);
                setTranscodeStatus("Pending");
                setMp4TranscodeStatus("Pending");
                await fetchUploadedCourses(savedMaterialId);
                await getLiveData();
              }
              setMessage("✅ Video uploaded & saved successfully!");
            } catch (sjErr) {
              console.error("Student Journey save error:", sjErr);
              setMessage("⚠️ Video uploaded, but failed to save to Student Journey.");
            }

            setLoading(false);
            return;
          }

          if (job.status === "failed") {
            clearInterval(pollRef.current);
            pollRef.current = null;
            if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
            setMessage(`❌ Upload failed: ${job.error || "Unknown error"}`);
            setLoading(false);
            setProgress(0);
            return;
          }
        } catch (pollErr) {
          if (controller.signal.aborted) return;
          console.error("Status poll error:", pollErr);
          // Keep polling on network hiccups; don't clear immediately
          consecutiveErrors += 1;
          const retryDelay = Math.min(3000 * 2 ** Math.min(consecutiveErrors, 4), 30000);
          nextPollAt = Date.now() + retryDelay;
          setMessage(
            `Cannot reach the upload status service (network, CORS, or gateway error). ` +
            `The upload may still be running. Retrying in ${retryDelay / 1000} seconds. Job ID: ${jobId}`
          );
        } finally {
          requestInFlight = false;
        }
      }, 3000);

      // Stop polling after 15 minutes
      pollTimeoutRef.current = setTimeout(() => {
        if (pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
        controller.abort();
        setLoading(false);
        setMessage(
          `Upload status monitoring stopped after 15 minutes; this does not mean the upload failed. ` +
          `Ask support to check job ${jobId} before starting another upload.`
        );
      }, 15 * 60 * 1000);
    } catch (err) {
      console.error("Upload start error:", err);
      setMessage(
        [429, 503].includes(err.response?.status)
          ? err.response.data?.error || "Upload service is busy. Please try again shortly."
          : "Could not confirm the upload started. Contact support before retrying."
      );
      setProgress(0);
      setLoading(false);
    }
  };

  // � Reupload: clone the existing material row to reset transcode jobs
  const handleReupload = () => {
    if (!reuploadAllowed || loading) return;
    if (!materialId) {
      setMessage("⚠️ Material ID missing. Cannot reupload.");
      return;
    }
    setShowReuploadModal(true);
  };

  const confirmReupload = async () => {
    setShowReuploadModal(false);
    if (!reuploadAllowed || loading) return;
    setLoading(true);
    setMessage("⏳ Re-uploading recording…");

    try {
      const res = await axios.post(
        "/reuploadLiveVideo",
        { materialId },
        {
          headers: {
            autherisation: `V9YlI4AgRJa97h9JT45acmijLliEcCEMnxZLtL7VlnWVETZuitk2MYoj0pCcrmHa`,
          },
        }
      );

      if (res.data?.success) {
        setReuploadAllowed(false);
        setReuploadWaitSeconds(5 * 60 * 60);
        setMaterialId(res.data.materialId);
        setTranscodeStatus("Pending");
        setMp4TranscodeStatus("Pending");
        setMessage("✅ Re-uploaded successfully! Transcoding restarted.");
        await getLiveData();
      } else {
        setMessage(`❌ Re-upload failed: ${res.data?.error || "Unknown error"}`);
      }
    } catch (err) {
      console.error("Re-upload error:", err);
      setMessage(err.response?.data?.error || "Re-upload failed.");
      if (err.response?.status === 403) {
        setReuploadAllowed(false);
        setReuploadWaitSeconds(err.response.data.reuploadWaitSeconds ?? null);
        setReuploadReason(err.response.data.error || "Re-upload is not available yet.");
        await getLiveData();
      }
    } finally {
      setLoading(false);
    }
  };

  // �📩 Save to DB
  const storeToStudentJourney = async (oracleUrl) => {
    try {
      const updatedUrl = oracleUrl.replace(
        "/p/jIul8gp04uV3Vwlb3zpEhXkRU1NVb8KdpdLMRya9OzTvgDs85JfcSd_kezfBr4IA",
        ""
      );

      const res = await axios.post(
        "/addLiveVideo",
        {
          course_ids: selectedCourses,
          topics: liveData.topic,
          subtopic: liveData.subtopic,
          caption: liveData.caption,
          materialLink: updatedUrl,
          youtubeLink: "",
          is_free: "0",
          date: liveData.date,
          chapters: [],
          meeting_id: liveId,
        },
        {
          headers: {
            autherisation: `V9YlI4AgRJa97h9JT45acmijLliEcCEMnxZLtL7VlnWVETZuitk2MYoj0pCcrmHa`,
          },
        }
      );

      return res.data?.material_id || "";
    } catch (error) {
      console.error("Error storing to student journey:", error);
      throw error;
    }
  };

  // 🎓 Fetch course titles for the uploaded material
  const fetchUploadedCourses = async (id) => {
    try {
      const res = await axios.get(`/liveVideoCourses/${id}`, {
        headers: {
          autherisation: `V9YlI4AgRJa97h9JT45acmijLliEcCEMnxZLtL7VlnWVETZuitk2MYoj0pCcrmHa`,
        },
      });
      console.log("res", res);
      if (res.data?.success) {
        setUploadedCourses(res.data.courses || []);
      }
    } catch (err) {
      console.error("Error fetching uploaded courses:", err);
    }
  };

  // 📄 Upload single file to S3
  const uploadFileToS3 = async (file) => {
    const fileName = `${Date.now()}-${file.name}`;
    const subject = liveData?.subject || "general";
    const folderPath = `learning_materials/uploads/materials/${subject}/`;
    const key = `${folderPath}${fileName}`;

    const s3Client = new S3Client({
      region: process.env.REACT_APP_AWS_REGION,
      credentials: {
        accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID,
        secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY,
      },
    });

    const parallelUploads3 = new Upload({
      client: s3Client,
      params: {
        Bucket: process.env.REACT_APP_AWS_S3_BUCKET,
        Key: key,
        Body: file,
        ContentType: file.type,
      },
    });

    await parallelUploads3.done();
    const s3Url = key.replace(/^learning_materials\//, "");
    return s3Url;
  };

  // 📄 Handle PDF upload flow
  const handlePdfUpload = async () => {
    if (!pdfFiles.length) {
      setPdfMessage("⚠️ Please select at least one PDF file.");
      return;
    }
    if (!selectedCourses.length) {
      setPdfMessage("⚠️ No courses available.");
      return;
    }

    setPdfLoading(true);
    setPdfMessage("⏳ Uploading PDFs...");

    try {
      for (const file of pdfFiles) {
        const materialLink = await uploadFileToS3(file);

        for (const course_id of selectedCourses) {
          await axios.post(
            "/add-newpdf",
            {
              sl_no: "0",
              course_id,
              topics: liveData.topic,
              subtopic: liveData.subtopic,
              caption: file.name.replace(/\.pdf$/i, ""),
              materialLink,
              is_free: 0,
              date: liveData.date,
              meeting_id: liveId,
            },
            {
              headers: {
                autherisation: `V9YlI4AgRJa97h9JT45acmijLliEcCEMnxZLtL7VlnWVETZuitk2MYoj0pCcrmHa`,
              },
            }
          );
        }
      }

      setPdfMessage("✅ All PDFs uploaded & saved successfully!");
      setPdfFiles([]);
      await getLiveData();
    } catch (err) {
      console.error("PDF upload error:", err);
      setPdfMessage("❌ PDF upload failed.");
    } finally {
      setPdfLoading(false);
    }
  };

  console.log("liveData",liveData);
  

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #dbeafe 0%, #e0e7ff 50%, #f3e8ff 100%)",
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        padding: "40px 20px",
      }}
    >
      <style>{`
        .upload-card {
          width: 100%;
          max-width: 520px;
          transition: max-width 0.3s ease;
        }
        @media (min-width: 1024px) {
          .upload-card {
            max-width: 900px;
          }
        }
        .uploaded-grid {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }
        @media (min-width: 1024px) {
          .uploaded-grid {
            flex-direction: row;
          }
          .uploaded-video-col {
            flex: 1.6;
            min-width: 0;
          }
          .uploaded-pdfs-col {
            flex: 1;
            min-width: 240px;
          }
        }
        .upload-btn {
          transition: all 0.2s ease;
        }
        .upload-btn:hover:not(:disabled) {
          background-color: #4338ca !important;
          transform: translateY(-1px);
          box-shadow: 0 8px 16px rgba(79, 70, 229, 0.25);
        }
      `}</style>
      <div
        className="upload-card"
        style={{
          background: "white",
          padding: "32px",
          borderRadius: 20,
          boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
        }}
      >
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <h2 style={{ margin: "0 0 6px 0", fontWeight: 800, fontSize: 22, color: "#1f2937" }}>
            🎥 Zoom → ✍️ Student Journey
          </h2>
          <p style={{ margin: 0, color: "#6b7280", fontSize: 14 }}>
            Upload your Zoom recording to Oracle and link it to courses
          </p>
        </div>

        <label style={{ fontWeight: 600, color: "#374151", fontSize: 14 }}>Zoom Meeting ID</label>
        <input
          type="text"
          value={liveId}
          readOnly
          style={{
            width: "100%",
            padding: "12px 14px",
            marginTop: 6,
            marginBottom: 22,
            border: "1px solid #d1d5db",
            borderRadius: 10,
            backgroundColor: "#f9fafb",
            color: "#374151",
            fontSize: 14,
            fontWeight: 500,
            outline: "none",
          }}
        />

        {isUploaded ? (
          <div
            style={{
              marginTop: 10,
              padding: 20,
              borderRadius: 16,
              backgroundColor: "#f0fdf4",
              border: "1px solid #bbf7d0",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginBottom: 18,
              }}
            >
              <span style={{ fontSize: 24 }}>✅</span>
              <div>
                <h4 style={{ margin: 0, fontWeight: 700, color: "#166534", fontSize: 18 }}>
                  Recording Uploaded
                </h4>
                <p style={{ margin: "4px 0 0 0", fontSize: 13, color: "#15803d" }}>
                  This live class video is already saved to Student Journey.
                </p>
              </div>
            </div>

            {uploadedCourses.length > 0 && (
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 8,
                  marginBottom: 18,
                  padding: 14,
                  backgroundColor: "#ffffff",
                  borderRadius: 12,
                  border: "1px solid #bbf7d0",
                }}
              >
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: "#166534",
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  Added to Courses:
                </span>
                {uploadedCourses.map((course, index) => (
                  <span
                    key={index}
                    style={{
                      fontSize: 12,
                      padding: "3px 10px",
                      borderRadius: 20,
                      backgroundColor: "#dcfce7",
                      color: "#166534",
                      fontWeight: 600,
                    }}
                  >
                    {course.course_title || course.course_id}
                  </span>
                ))}
              </div>
            )}

            <div className="uploaded-grid">
              <div className="uploaded-video-col">
                {uploadedUrl && (
                  <video
                    src={uploadedUrl}
                    controls
                    style={{
                      width: "100%",
                      borderRadius: 12,
                      boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                      display: "block",
                    }}
                  />
                )}

                <div
                  style={{
                    display: "flex",
                    gap: 10,
                    marginTop: 14,
                    flexWrap: "wrap",
                  }}
                >
                  {[
                    { label: "Transcode Upload", value: transcodeStatus },
                    { label: "Transcode Download", value: mp4TranscodeStatus },
                  ].map((item, idx) => {
                    const badge = getStatusBadge(item.value);
                    return (
                      <div
                        key={idx}
                        style={{
                          flex: 1,
                          minWidth: 140,
                          padding: 10,
                          backgroundColor: "#ffffff",
                          borderRadius: 10,
                          border: "1px solid #bbf7d0",
                        }}
                      >
                        <p
                          style={{
                            margin: "0 0 6px 0",
                            fontSize: 11,
                            color: "#64748b",
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                            fontWeight: 600,
                          }}
                        >
                          {item.label}
                        </p>
                        <span
                          style={{
                            display: "inline-block",
                            padding: "4px 10px",
                            borderRadius: 20,
                            backgroundColor: badge.bg,
                            color: badge.color,
                            fontSize: 12,
                            fontWeight: 700,
                          }}
                        >
                          {badge.label}
                        </span>
                      </div>
                    );
                  })}
                </div>

                {![transcodeStatus, mp4TranscodeStatus].every(
                  (status) => String(status || "").trim().toLowerCase() === "completed"
                ) && (
                  <>
                    <button
                      onClick={handleReupload}
                      disabled={loading || !reuploadAllowed}
                      style={{
                        width: "100%",
                        padding: 12,
                        marginTop: 14,
                        fontWeight: 600,
                        color: "white",
                        background: loading || !reuploadAllowed ? "#9ca3af" : "#4f46e5",
                        borderRadius: 10,
                        border: "none",
                        cursor: loading || !reuploadAllowed ? "not-allowed" : "pointer",
                        transition: "all 0.2s ease",
                      }}
                    >
                      {loading ? "Processing…" : "🔄 Re-upload Recording"}
                    </button>
                    <p
                      style={{
                        marginTop: 8,
                        textAlign: "center",
                        fontSize: 12,
                        color: "#64748b",
                        lineHeight: 1.4,
                      }}
                    >
                      {reuploadAllowed
                        ? "If transcoding is stuck, re-upload will duplicate the video entry and restart transcoding."
                        : reuploadWaitSeconds > 0
                        ? "Re-upload is disabled until five hours after this video uploaded time. It will unlock automatically after verification."
                        : reuploadReason || "Re-upload is disabled until the material's upload time can be verified."}
                    </p>
                  </>
                )}
              </div>

              <div className="uploaded-pdfs-col">
                {uploadedPdfs.length > 0 ? (
                  <div
                    style={{
                      backgroundColor: "#ffffff",
                      borderRadius: 12,
                      padding: 16,
                      border: "1px solid #bbf7d0",
                      height: "100%",
                    }}
                  >
                    <h5
                      style={{
                        margin: "0 0 12px 0",
                        fontWeight: 700,
                        color: "#166534",
                        fontSize: 14,
                      }}
                    >
                      📑 Uploaded PDFs
                    </h5>
                    <ul style={{ paddingLeft: 0, margin: 0, listStyle: "none" }}>
                      {uploadedPdfs.map((pdf, index) => (
                        <li
                          key={index}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "10px 12px",
                            marginBottom: 8,
                            backgroundColor: "#f8fafc",
                            borderRadius: 8,
                            border: "1px solid #e2e8f0",
                          }}
                        >
                          <span
                            style={{
                              fontSize: 13,
                              color: "#475569",
                              flex: 1,
                              marginRight: 10,
                            }}
                          >
                            📄 {pdf.caption || `PDF ${index + 1}`}
                          </span>
                          <a
                            href={pdf.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              fontSize: 12,
                              color: "#4f46e5",
                              fontWeight: 600,
                              textDecoration: "none",
                              whiteSpace: "nowrap",
                            }}
                          >
                            View →
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <div
                    style={{
                      backgroundColor: "#ffffff",
                      borderRadius: 12,
                      padding: 16,
                      border: "1px solid #bbf7d0",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      textAlign: "center",
                    }}
                  >
                    <span style={{ fontSize: 28, marginBottom: 8 }}>📑</span>
                    <p style={{ margin: 0, fontSize: 13, color: "#64748b" }}>
                      No PDFs uploaded yet.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <>
            {!uploadedPdfs.length && (
              <div
                style={{
                  textAlign: "center",
                  padding: "26px 20px",
                  marginBottom: 20,
                  backgroundColor: "#f8fafc",
                  borderRadius: 14,
                  border: "1px dashed #cbd5e1",
                }}
              >
                <span style={{ fontSize: 40, display: "block", marginBottom: 10 }}>🎬</span>
                <p style={{ margin: 0, fontWeight: 700, color: "#334155", fontSize: 15 }}>
                  No recording uploaded yet
                </p>
                <p style={{ margin: "6px 0 0 0", fontSize: 13, color: "#64748b" }}>
                  Upload the Zoom recording for this live class to get started.
                </p>
              </div>
            )}

            <button
              className="upload-btn"
              onClick={handleUpload}
              disabled={loading}
              style={{
                width: "100%",
                padding: 14,
                fontWeight: 600,
                color: "white",
                background: loading ? "#9ca3af" : "#4f46e5",
                borderRadius: 10,
                border: "none",
                cursor: loading ? "not-allowed" : "pointer",
              }}
            >
              {loading ? "Processing…" : "Upload Recording"}
            </button>

            <p
              style={{
                textAlign: "center",
                color: "#6b7280",
                fontSize: 13,
                marginTop: 12,
                marginBottom: 0,
                lineHeight: 1.4,
              }}
            >
              Clicking this button will automatically fetch the Zoom recording for this meeting and upload it to Student Journey.
            </p>

            {message && (
              <p
                style={{
                  marginTop: 15,
                  fontWeight: 600,
                  textAlign: "center",
                  color: message.includes("❌")
                    ? "red"
                    : message.includes("⏳")
                    ? "gray"
                    : "green",
                }}
              >
                {message}
              </p>
            )}

            {loading && (
              <div style={{ marginTop: 15 }}>
                <div
                  style={{
                    width: "100%",
                    height: 12,
                    backgroundColor: "#e5e7eb",
                    borderRadius: 6,
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      width: `${progress}%`,
                      height: "100%",
                      backgroundColor: "#4f46e5",
                      transition: "width 0.3s ease",
                    }}
                  />
                </div>
                <p
                  style={{
                    marginTop: 6,
                    textAlign: "center",
                    fontSize: 13,
                    color: "#6b7280",
                  }}
                >
                  {progress}%
                </p>
              </div>
            )}
          </>
        )}

        <hr style={{ margin: "25px 0", border: 0, borderTop: "1px solid #e5e7eb" }} />

        <h4 style={{ textAlign: "center", marginBottom: 6, fontSize: 17, fontWeight: 700 }}>
          📄 {uploadedPdfs.length > 0 ? "Add More PDFs" : "Add PDF Materials"}
        </h4>
        <p
          style={{
            textAlign: "center",
            color: "#6b7280",
            fontSize: 13,
            marginBottom: 20,
            marginTop: 0,
          }}
        >
          {uploadedPdfs.length > 0
            ? "Upload additional PDF materials for this live class"
            : "Attach PDF materials to this live class"}
        </p>

        <label
          style={{
            display: "block",
            width: "100%",
            padding: "24px 16px",
            border: "2px dashed #cbd5e1",
            borderRadius: 12,
            textAlign: "center",
            cursor: "pointer",
            backgroundColor: "#f8fafc",
            transition: "all 0.2s ease",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = "#94a3b8";
            e.currentTarget.style.backgroundColor = "#f1f5f9";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = "#cbd5e1";
            e.currentTarget.style.backgroundColor = "#f8fafc";
          }}
        >
          <span style={{ fontSize: 28, display: "block", marginBottom: 6 }}>📁</span>
          <span style={{ fontSize: 14, fontWeight: 600, color: "#475569" }}>
            Click to select PDF files
          </span>
          <span style={{ fontSize: 12, color: "#94a3b8", display: "block", marginTop: 4 }}>
            or drag and drop PDFs here
          </span>
          <input
            type="file"
            accept="application/pdf"
            multiple
            onChange={(e) => {
              const newFiles = Array.from(e.target.files);
              setPdfFiles((prev) => [...prev, ...newFiles]);
              e.target.value = "";
            }}
            style={{ display: "none" }}
          />
        </label>

        {pdfFiles.length > 0 && (
          <ul style={{ fontSize: 13, marginTop: 12, marginBottom: 10, paddingLeft: 18, listStyle: "none" }}>
            {pdfFiles.map((f, i) => (
              <li key={i} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                📄 {f.name}
                <button
                  type="button"
                  onClick={() => setPdfFiles((prev) => prev.filter((_, idx) => idx !== i))}
                  style={{
                    background: "#ef4444",
                    color: "white",
                    border: "none",
                    borderRadius: 4,
                    padding: "2px 8px",
                    cursor: "pointer",
                    fontSize: 12,
                  }}
                >
                  ✕
                </button>
              </li>
            ))}
          </ul>
        )}

        <button
          onClick={handlePdfUpload}
          disabled={pdfLoading || pdfFiles.length === 0}
          style={{
            width: "100%",
            padding: 14,
            marginTop: 14,
            fontWeight: 600,
            color: "white",
            background: pdfLoading || pdfFiles.length === 0 ? "#9ca3af" : "#16a34a",
            borderRadius: 10,
            border: "none",
            cursor: pdfLoading || pdfFiles.length === 0 ? "not-allowed" : "pointer",
            transition: "all 0.2s ease",
          }}
        >
          {pdfLoading
            ? "Uploading PDFs…"
            : uploadedPdfs.length > 0
            ? "Upload More PDFs"
            : "Upload PDFs"}
        </button>

        {pdfMessage && (
          <p
            style={{
              marginTop: 15,
              fontWeight: 600,
              textAlign: "center",
              color: pdfMessage.includes("❌")
                ? "red"
                : pdfMessage.includes("⏳")
                ? "gray"
                : "green",
            }}
          >
            {pdfMessage}
          </p>
        )}

      </div>

      {showReuploadModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
            padding: 20,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowReuploadModal(false);
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: 16,
              padding: 28,
              width: "100%",
              maxWidth: 400,
              boxShadow: "0 20px 40px rgba(0,0,0,0.2)",
              textAlign: "center",
            }}
          >
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                backgroundColor: "#fee2e2",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 16px",
                fontSize: 28,
              }}
            >
              🔄
            </div>
            <h3
              style={{
                margin: "0 0 10px 0",
                fontSize: 18,
                fontWeight: 700,
                color: "#1f2937",
              }}
            >
              Re-upload Recording?
            </h3>
            <p
              style={{
                margin: "0 0 24px 0",
                fontSize: 14,
                color: "#4b5563",
                lineHeight: 1.5,
              }}
            >
              This will duplicate the video entry, replace the existing one, and
              restart transcoding. Linked courses and PDFs will remain intact.
            </p>
            <div
              style={{
                display: "flex",
                gap: 12,
                justifyContent: "center",
              }}
            >
              <button
                onClick={() => setShowReuploadModal(false)}
                style={{
                  padding: "10px 20px",
                  borderRadius: 8,
                  border: "1px solid #d1d5db",
                  backgroundColor: "white",
                  color: "#374151",
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmReupload}
                disabled={loading || !reuploadAllowed}
                style={{
                  padding: "10px 20px",
                  borderRadius: 8,
                  border: "none",
                  backgroundColor: loading ? "#9ca3af" : "#4f46e5",
                  color: "white",
                  fontWeight: 600,
                  cursor: loading ? "not-allowed" : "pointer",
                  transition: "all 0.2s ease",
                }}
              >
                {loading ? "Processing…" : "Yes, Re-upload"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ZoomToOracleUpload;
